import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  mappingService,
  CollaboDataType,
  CollaboDataField,
  BugyoDataField,
  MappingField,
  MappingRegistrationForm as MappingFormData,
  MappingRegistrationRequest,
  FieldMappingItem,
  FieldMappingUpdateItem,
  BugyoDataType,
  BugyoDataTypeLabels,
  FieldType,
  MappingFieldName,
  CollaboTable,
} from "../services/mappingService";
import { useUnsavedChanges } from "../services/unsavedChangesService";
import config from "../config/env";
import Header from "./Header";
import BackNav from "./BackNav";
import Loading from "./Loading";
import SearchableSelect from "./SearchableSelect";
import ConfirmationModal from "./ConfirmationModal";
import "./MappingRegistrationForm.css";

interface MappingRegistrationFormProps {
  user: any;
  onLogout: () => void;
}

const MappingRegistrationForm: React.FC<MappingRegistrationFormProps> = ({
  user,
  onLogout,
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  // Error boundary state
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Track previous values to detect real changes
  const prevCollaboFormId = useRef<number>(0);
  const prevBugyoDataType = useRef<string>("");

  // Form state
  const [formData, setFormData] = useState<MappingFormData>({
    collaboFormName: "",
    collaboFormId: 0,
    bugyoDataType: "",
    headerMappingJson: { mappingList: [] },
    detailsMappingJson: {},
    dataListInfoJson: {},
    fieldMappings: [],
  });

  // Internal state for UI management
  const [headerMappings, setHeaderMappings] = useState<MappingField[]>([]);
  const [detailMappings, setDetailMappings] = useState<MappingField[]>([]); // Keep for backward compatibility
  const [tableMappings, setTableMappings] = useState<
    Record<string, MappingField[]>
  >({}); // New: mappings per table
  const [dataListInfoMappings, setDataListInfoMappings] = useState<
    MappingField[]
  >([]);

  // Data state
  const [collaboDataTypes, setCollaboDataTypes] = useState<CollaboDataType[]>(
    []
  );
  const [bugyoDataTypes, setBugyoDataTypes] = useState<BugyoDataType[]>([]);
  const [collaboDataFields, setCollaboDataFields] = useState<
    CollaboDataField[]
  >([]);
  const [collaboDataTables, setCollaboDataTables] = useState<CollaboTable[]>(
    []
  );
  const [bugyoDataFields, setBugyoDataFields] = useState<BugyoDataField[]>(
    []
  );

  // Mapping cache state - stores mappings per type combination
  interface MappingCache {
    [key: string]: {
      headerMappings: MappingField[];
      detailMappings: MappingField[];
      dataListInfoMappings: MappingField[];
      tableMappings: Record<string, MappingField[]>;
      activeTab: string;
    };
  }
  const [mappingCache, setMappingCache] = useState<MappingCache>({});

  // UI state
  const [activeTab, setActiveTab] = useState<string>("header");
  const [loading, setLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Track mappings with missing Collabo fields
  const [missingFieldWarnings, setMissingFieldWarnings] = useState<
    Record<string, string>
  >({});

  // Separate loading states for better UX
  const [dataLoading, setDataLoading] = useState(false);

  // Tab error state for visual indicators (dynamic to support table tabs)
  const [tabErrors, setTabErrors] = useState<Record<string, boolean>>({
    header: false,
    dataListInfo: false,
    // Table tabs will be added dynamically: 'detail-table-{tableKey}': boolean
  });

  // Tab error messages for detailed display (dynamic to support table tabs)
  const [tabErrorMessages, setTabErrorMessages] = useState<
    Record<string, string[]>
  >({
    header: [],
    dataListInfo: [],
    // Table tabs will be added dynamically: 'detail-table-{tableKey}': string[]
  });

  // Invalid settings warning state (for 401/500 authentication errors)
  const [showInvalidSettingsWarning, setShowInvalidSettingsWarning] =
    useState(false);

  // Webhook token state (edit mode only)
  const [webhookToken, setWebhookToken] = useState<string | undefined>(
    undefined
  );
  const [webhookCopied, setWebhookCopied] = useState(false);
  const [regeneratingToken, setRegeneratingToken] = useState(false);
  const [showRegenerateConfirmationModal, setShowRegenerateConfirmationModal] = useState(false);

  /** Webhook URL を構築する */
  const getWebhookUrl = (token: string): string => {
    if (config.webhookBaseUrl) {
      const baseUrl = config.webhookBaseUrl.replace(/\/+$/, "");
      return `${baseUrl}/webhook/${token}`;
    }
    const apiUrl = config.apiUrl.replace(/\/+$/, "");
    const baseUrl = apiUrl.startsWith("http")
      ? apiUrl
      : `${window.location.origin}${apiUrl}`;
    return `${baseUrl}/webhook/${token}`;
  };

  /** クリップボードにコピー */
  const handleCopyWebhookUrl = async () => {
    if (!webhookToken) return;
    try {
      await navigator.clipboard.writeText(getWebhookUrl(webhookToken));
      setWebhookCopied(true);
      setTimeout(() => setWebhookCopied(false), 2000);
    } catch {
      // fallback
      const textArea = document.createElement("textarea");
      textArea.value = getWebhookUrl(webhookToken);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setWebhookCopied(true);
      setTimeout(() => setWebhookCopied(false), 2000);
    }
  };

  /** Webhook トークン再生成 */
  const handleRegenerateWebhookToken = () => {
    if (!id) return;
    setShowRegenerateConfirmationModal(true);
  };

  const confirmRegenerateWebhookToken = async () => {
    setShowRegenerateConfirmationModal(false);
    if (!id) return;
    try {
      setRegeneratingToken(true);
      const response = await mappingService.regenerateWebhookToken(
        parseInt(id)
      );
      if (response.success && response.data?.webhookToken) {
        setWebhookToken(response.data.webhookToken);
      }
    } catch (error) {
      console.error("Error regenerating webhook token:", error);
    } finally {
      setRegeneratingToken(false);
    }
  };

  // Use unsaved changes service
  const { handleLogoClick, handleLogoutClick } = useUnsavedChanges({
    hasUnsavedChanges,
    warningMessage: "変更が保存されていません。このページを離れますか？",
  });

  // Helper functions for mapping cache management
  const getCacheKey = (collaboFormId: number, bugyoDataType: string) => {
    return `${collaboFormId}-${bugyoDataType}`;
  };

  const saveCurrentMappingsToCache = () => {
    if (formData.collaboFormId && formData.bugyoDataType) {
      const cacheKey = getCacheKey(
        formData.collaboFormId,
        formData.bugyoDataType
      );
      setMappingCache(prev => ({
        ...prev,
        [cacheKey]: {
          headerMappings: [...headerMappings],
          detailMappings: [...detailMappings],
          dataListInfoMappings: [...dataListInfoMappings],
          tableMappings: { ...tableMappings },
          activeTab,
        },
      }));
    }
  };

  const loadMappingsFromCache = (
    collaboFormId: number,
    bugyoDataType: string
  ) => {
    const cacheKey = getCacheKey(collaboFormId, bugyoDataType);
    const cached = mappingCache[cacheKey];

    if (cached) {
      setHeaderMappings(cached.headerMappings);
      setDetailMappings(cached.detailMappings);
      setDataListInfoMappings(cached.dataListInfoMappings);
      setTableMappings(cached.tableMappings || {});
      setActiveTab(cached.activeTab);
      return true;
    }
    return false;
  };

  const clearMappingCache = () => {
    setMappingCache({});
  };

  // Helper to get table ID from tab name
  const getTableIdFromTab = (tab: string): string | null => {
    if (tab.startsWith("detail-table-")) {
      return tab.replace("detail-table-", "");
    }
    return null;
  };

  // Helper to find a collabo field in all possible locations (direct fields + table fields)
  const findCollaboField = (
    fieldName: string,
    fieldKey: string,
    fieldsData: { fields: CollaboDataField[]; tables: CollaboTable[] }
  ): CollaboDataField | null => {
    // Search in direct fields first
    let field =
      fieldsData.fields.find(f => f.name === fieldName) ||
      fieldsData.fields.find(f => f.key === fieldKey);

    // If not found, search in table fields
    if (!field) {
      for (const table of fieldsData.tables) {
        field =
          table.fields.find(f => f.name === fieldName) ||
          table.fields.find(f => f.key === fieldKey);
        if (field) break;
      }
    }

    return field || null;
  };

  // Available fields for selection (now allows multiple selections of the same field)
  const getAvailableCollaboFields = (tab?: string) => {
    // If it's a detail tab for a specific table, filter by that table's fields
    if (tab && tab.startsWith("detail-table-")) {
      const tableKey = getTableIdFromTab(tab);
      const table = collaboDataTables.find(t => t.key === tableKey);
      if (table && table.fields) {
        return table.fields;
      }
      return [];
    }

    // For header tab, return non-table fields
    if (tab === "header") {
      // Filter out fields that belong to any table
      const tableFieldNames = new Set(
        collaboDataTables.flatMap(
          t => t.fields?.map((f: CollaboDataField) => f.name) || []
        )
      );
      return collaboDataFields.filter(f => !tableFieldNames.has(f.name));
    }

    // Default: return all fields
    return collaboDataFields;
  };

  const getAvailableBugyoFields = (
    tab: string,
    currentMapping?: MappingField
  ) => {
    const currentTabMappings =
      tab === "header"
        ? headerMappings
        : tab === "dataListInfo"
          ? dataListInfoMappings
          : tab.startsWith("detail-table-")
            ? tableMappings[tab] || []
            : detailMappings;
    const selectedFields = currentTabMappings
      .filter(mapping => !mapping.isDeleted) // Exclude deleted mappings
      .map(m => m.bugyoField)
      .filter(Boolean);

    const dataInfoType =
      tab === "header"
        ? "HEADER"
        : tab === "dataListInfo"
          ? "DATA_LIST_INFO"
          : "DETAIL"; // All table tabs use DETAIL
    const filteredFields = bugyoDataFields.filter(
      field => field.dataInfo === dataInfoType
    );

    // Always include the current mapping's selected field if it exists
    const availableFields = filteredFields.filter(field => {
      // Include if not selected by any other mapping in the same tab
      const isSelectedByOthers =
        selectedFields.includes(field.fieldName) &&
        field.fieldName !== currentMapping?.bugyoField;
      // Or if it's the current mapping's selection
      const isCurrentSelection =
        field.fieldName === currentMapping?.bugyoField;

      return !isSelectedByOthers || isCurrentSelection;
    });

    return availableFields;
  };

  // Check app_cd before loading data
  const loadData = async () => {
    try {
      setDataLoading(true);

      if (isEdit && id) {
        await loadMappingData(parseInt(id));
      } else {
        // For create mode, just load initial data
        await loadInitialData();
      }

      setDataLoading(false);
    } catch (error: any) {
      console.error("Error checking app_cd:", error);

      // Handle timeout errors specifically
      if (error.isTimeout) {
        setDataLoading(false);
        return;
      }
      setDataLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [isEdit, id]);

  useEffect(() => {
    if (formData.collaboFormId > 0) {
      // Always load fields
      loadCollaboDataFields();

      // Update previous value
      prevCollaboFormId.current = formData.collaboFormId;
    }
  }, [formData.collaboFormId]);

  // Initialize tableMappings when collaboDataTables changes
  useEffect(() => {
    if (collaboDataTables && collaboDataTables.length > 0) {
      // Initialize empty mappings for each table if they don't exist
      setTableMappings(prev => {
        const newMappings = { ...prev };

        // Get current table keys
        const currentTableKeys = Object.keys(newMappings);
        const newTableKeys = collaboDataTables.map(
          table => `detail-table-${table.key}`
        );

        // If tableMappings is empty or doesn't match current tables, reinitialize
        const needsReinitialization =
          currentTableKeys.length === 0 ||
          !newTableKeys.some(key => currentTableKeys.includes(key));

        if (needsReinitialization) {
          // Clear old mappings and initialize new ones
          const freshMappings: Record<string, MappingField[]> = {};
          collaboDataTables.forEach(table => {
            const tabKey = `detail-table-${table.key}`;
            freshMappings[tabKey] = [];
          });
          return freshMappings;
        } else {
          // Just add missing tables without clearing existing ones
          collaboDataTables.forEach(table => {
            const tabKey = `detail-table-${table.key}`;
            if (!newMappings[tabKey]) {
              newMappings[tabKey] = [];
            }
          });
          return newMappings;
        }
      });
    }
  }, [collaboDataTables]);

  useEffect(() => {
    if (formData.bugyoDataType) {
      // Always load fields
      loadBugyoDataFields();

      // Update previous value
      prevBugyoDataType.current = formData.bugyoDataType;
    }
  }, [formData.bugyoDataType]);

  // Validate tables existence when both form and data type are selected
  useEffect(() => {
    // Only validate if both are selected, type is not CUSTOMER_DATA, and not loading
    if (
      formData.collaboFormId > 0 &&
      formData.bugyoDataType &&
      formData.bugyoDataType !== BugyoDataType.CUSTOMER_DATA &&
      !loadingFields
    ) {
      // Check if tables exist
      if (!collaboDataTables || collaboDataTables.length === 0) {
        setErrors(prev => ({
          ...prev,
          noTables: "申請書に明細項目が存在しないため、登録できません",
        }));
      } else {
        // Clear the error if tables exist
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.noTables;
          return newErrors;
        });
      }
    } else {
      // Clear the error if conditions are not met
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.noTables;
        return newErrors;
      });
    }
  }, [
    formData.collaboFormId,
    formData.bugyoDataType,
    collaboDataTables,
    loadingFields,
  ]);

  // Error boundary effect
  useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error("Global error caught:", error);
      setHasError(true);
      setErrorMessage(error.message || "An unexpected error occurred");
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, []);

  // Debug effect to track state changes
  useEffect(() => {
    console.log("State changed:", {
      formData: {
        collaboFormId: formData.collaboFormId,
        collaboFormName: formData.collaboFormName,
        bugyoDataType: formData.bugyoDataType,
      },
      headerMappings: headerMappings.filter(m => !m.isDeleted).length,
      detailMappings: detailMappings.filter(m => !m.isDeleted).length,
      dataListInfoMappings: dataListInfoMappings.filter(m => !m.isDeleted)
        .length,
      collaboDataTypes: collaboDataTypes.length,
      bugyoDataTypes: bugyoDataTypes.length,
      collaboDataFields: collaboDataFields.length,
      bugyoDataFields: bugyoDataFields.length,
      canSave: canSave(),
      errors: Object.keys(errors),
      loading: loading,
      dataLoading: dataLoading,
    });
  }, [
    headerMappings,
    detailMappings,
    dataListInfoMappings,
    errors,
    formData.collaboFormId,
    formData.bugyoDataType,
    formData.collaboFormName,
    collaboDataTypes,
    bugyoDataTypes,
    collaboDataFields,
    bugyoDataFields,
    loading,
    dataLoading,
  ]);

  const loadInitialData = async (excludeFormId?: number) => {
    try {
      // Don't set loading here as it's handled by dataLoading state
      const [collaboResponse, bugyoResponse] = await Promise.all([
        mappingService.getCollaboDataTypes(excludeFormId),
        mappingService.getBugyoDataFieldTypes(),
      ]);

      if (collaboResponse.success) {
        setCollaboDataTypes(collaboResponse.data);
      }
      if (bugyoResponse.success) {
        // Convert string array to enum array
        const enumDataTypes = bugyoResponse.data.map(
          type => type as BugyoDataType
        );
        setBugyoDataTypes(enumDataTypes);
      }
    } catch (error: any) {
      console.error("Error loading initial data:", error);
      console.log("Error details:", {
        status: error?.status,
        responseStatus: error?.response?.status,
        message: error?.message,
        isTimeout: error?.isTimeout,
      });

      // Handle timeout errors specifically
      if (error.isTimeout) {
        // For timeout, don't show warning popup, just log the error
        return;
      }

      // Check if error has 401 or 500 status code (authentication/authorization errors)
      if (
        error?.response?.status === 401 ||
        error?.status === 401 ||
        error?.response?.status === 500 ||
        error?.status === 500
      ) {
        // Show invalid settings warning for auth errors
        console.log(
          "Showing invalid settings warning for status:",
          error?.status || error?.response?.status
        );
        setShowInvalidSettingsWarning(true);
        setDataLoading(false);
        return;
      }
    }
  };

  const loadMappingData = async (mappingId: number) => {
    try {
      // Don't set loading here as it's handled by dataLoading state

      // Clear all existing mappings first to avoid mixing old and new data
      setHeaderMappings([]);
      setDetailMappings([]);
      setDataListInfoMappings([]);

      // First load mapping data to get the form ID and data type
      const mappingResponse =
        await mappingService.getMappingRegistration(mappingId);

      if (mappingResponse.success) {
        const mappingData = mappingResponse.data;

        // Load initial data with current form excluded from filtering first
        await loadInitialData(mappingData.collaboFormId);

        // Now load all field data in parallel using the mapping data
        const [collaboFieldsResponse, bugyoFieldsResponse] = await Promise.all(
          [
            mappingService.getCollaboDataFields(mappingData.collaboFormId),
            mappingService.getBugyoDataFields(mappingData.bugyoDataType),
          ]
        );

        // Set field data first
        if (collaboFieldsResponse.success) {
          setCollaboDataFields(collaboFieldsResponse.data.fields);
          setCollaboDataTables(collaboFieldsResponse.data.tables);
        }
        if (bugyoFieldsResponse.success) {
          setBugyoDataFields(bugyoFieldsResponse.data);
        }

        // Set form data after field data is loaded
        setFormData({
          collaboFormName: mappingData.collaboFormName,
          collaboFormId: mappingData.collaboFormId,
          bugyoDataType: mappingData.bugyoDataType,
          headerMappingJson: mappingData.headerMappingJson,
          detailsMappingJson: mappingData.detailsMappingJson,
          dataListInfoJson: mappingData.dataListInfoJson,
          fieldMappings: mappingData.fieldMappings || [],
        });

        // Set webhook token if available
        if (mappingData.webhookToken) {
          setWebhookToken(mappingData.webhookToken);
        }

        // Process fieldMappings if available (new structure)
        if (mappingData.fieldMappings && mappingData.fieldMappings.length > 0) {
          // Group fieldMappings by dataInfo and tableKey
          const headerMappings: MappingField[] = [];
          const detailMappings: MappingField[] = [];
          const dataListInfoMappings: MappingField[] = [];
          const newTableMappings: Record<string, MappingField[]> = {};
          const newMissingFieldWarnings: Record<string, string> = {};

          mappingData.fieldMappings.forEach((fieldMapping, mappingIndex) => {
            // Try to find by key first (more reliable), then by name as fallback
            let collaboField: CollaboDataField | null = null;
            let fieldMissing = false;

            if (collaboFieldsResponse.success) {
              // If tableKey exists, search in that specific table
              if (fieldMapping.tableKey) {
                const table = collaboFieldsResponse.data.tables.find(
                  t => t.key === fieldMapping.tableKey
                );
                if (table) {
                  collaboField =
                    table.fields.find(
                      f => f.key === fieldMapping.collaboFieldKey
                    ) ||
                    table.fields.find(
                      f => f.name === fieldMapping.collaboFieldName
                    ) ||
                    null;

                  // Check if field is missing
                  if (!collaboField) {
                    fieldMissing = true;
                  }
                }
              } else {
                // Search in direct fields
                collaboField = findCollaboField(
                  fieldMapping.collaboFieldName,
                  fieldMapping.collaboFieldKey,
                  collaboFieldsResponse.data
                );

                // Check if field is missing
                if (!collaboField) {
                  fieldMissing = true;
                }
              }
            }

            const bugyoField = bugyoFieldsResponse.success
              ? bugyoFieldsResponse.data.find(
                  f => f.fieldName === fieldMapping.bugyoFieldName
                )
              : null;

            // Skip this mapping if the Collabo field is missing
            if (fieldMissing) {
              // Add warning for this missing field
              const warningKey = `missing_field_${mappingIndex}`;
              newMissingFieldWarnings[warningKey] =
                `申請書項目「${fieldMapping.collaboFieldName}」が見つかりません。申請書のフォームが変更された可能性があります。`;
              return; // Skip adding this mapping to the list
            }

            const mappingField: MappingField = {
              id: fieldMapping.id,
              collaboField: collaboField?.name || fieldMapping.collaboFieldName, // Use current name if available
              bugyoField: fieldMapping.bugyoFieldName,
              collaboFieldType: collaboField?.convertType || FieldType.STRING,
              bugyoFieldType: bugyoField?.fieldType || FieldType.STRING,
              isNew: false,
              isModified: Boolean(
                collaboField &&
                  collaboField.name !== fieldMapping.collaboFieldName
              ), // Mark as modified if name changed
              isDeleted: false,
              originalCollaboField: fieldMapping.collaboFieldName,
              originalBugyoField: fieldMapping.bugyoFieldName,
            };

            if (fieldMapping.dataInfo === "HEADER") {
              headerMappings.push(mappingField);
            } else if (fieldMapping.dataInfo === "DETAIL") {
              // Check if it's a table mapping
              if (fieldMapping.tableKey) {
                const tabKey = `detail-table-${fieldMapping.tableKey}`;
                if (!newTableMappings[tabKey]) {
                  newTableMappings[tabKey] = [];
                }
                newTableMappings[tabKey].push(mappingField);
              } else {
                // Try to find which table this field belongs to
                let foundTable = false;
                if (
                  collaboFieldsResponse.success &&
                  collaboFieldsResponse.data.tables
                ) {
                  for (const table of collaboFieldsResponse.data.tables) {
                    const fieldInTable = table.fields.find(
                      f =>
                        f.key === fieldMapping.collaboFieldKey ||
                        f.name === fieldMapping.collaboFieldName
                    );
                    if (fieldInTable) {
                      const tabKey = `detail-table-${table.key}`;
                      if (!newTableMappings[tabKey]) {
                        newTableMappings[tabKey] = [];
                      }
                      newTableMappings[tabKey].push(mappingField);
                      foundTable = true;
                      break;
                    }
                  }
                }

                // If not found in any table, use old style detail mapping (backward compatibility)
                if (!foundTable) {
                  detailMappings.push(mappingField);
                }
              }
            } else if (fieldMapping.dataInfo === "DATA_LIST_INFO") {
              dataListInfoMappings.push(mappingField);
            }
          });

          // Set missing field warnings
          setMissingFieldWarnings(newMissingFieldWarnings);

          // Set new mappings directly (no need for setTimeout in React 18)

          setHeaderMappings(headerMappings);
          setDetailMappings(detailMappings);
          setDataListInfoMappings(dataListInfoMappings);
          setTableMappings(newTableMappings);

          // Auto switch to appropriate tab based on data type
          if (mappingData.bugyoDataType === BugyoDataType.CUSTOMER_DATA) {
            setActiveTab("dataListInfo");
          } else {
            // Default to header tab
            setActiveTab("header");
          }
        } else {
          // Fallback to old structure for backward compatibility
          const newMissingFieldWarnings: Record<string, string> = {};

          // Convert header mappings to UI format with correct field types
          const headerMappings: MappingField[] =
            mappingData.headerMappingJson.mappingList
              .map((item, index) => {
                const collaboField = collaboFieldsResponse.success
                  ? findCollaboField(
                      item.collaboFieldName,
                      "",
                      collaboFieldsResponse.data
                    )
                  : null;

                // Check if field is missing
                if (!collaboField) {
                  const warningKey = `missing_header_${index}`;
                  newMissingFieldWarnings[warningKey] =
                    `申請書項目「${item.collaboFieldName}」が見つかりません。申請書のフォームが変更された可能性があります。`;
                  return null; // Skip this mapping
                }

                const bugyoField = bugyoFieldsResponse.success
                  ? bugyoFieldsResponse.data.find(
                      f => f.fieldName === item.bugyoFieldName
                    )
                  : null;

                return {
                  collaboField: collaboField?.name || item.collaboFieldName, // Use current name if available
                  bugyoField: item.bugyoFieldName,
                  collaboFieldType: collaboField?.convertType || item.fieldType,
                  bugyoFieldType: bugyoField?.fieldType || item.fieldType,
                  isNew: false,
                  isModified: Boolean(
                    collaboField && collaboField.name !== item.collaboFieldName
                  ), // Mark as modified if name changed
                  isDeleted: false,
                  originalCollaboField: item.collaboFieldName,
                  originalBugyoField: item.bugyoFieldName,
                };
              })
              .filter(mapping => mapping !== null);
          setHeaderMappings(headerMappings);

          // Convert details mappings to UI format with correct field types
          const detailMappings: MappingField[] = Object.entries(
            mappingData.detailsMappingJson
          )
            .map(([collaboField, bugyoField], index) => {
              const collaboFieldData = collaboFieldsResponse.success
                ? findCollaboField(collaboField, "", collaboFieldsResponse.data)
                : null;

              // Check if field is missing
              if (!collaboFieldData) {
                const warningKey = `missing_detail_${index}`;
                newMissingFieldWarnings[warningKey] =
                  `申請書項目「${collaboField}」が見つかりません。申請書のフォームが変更された可能性があります。`;
                return null; // Skip this mapping
              }

              const bugyoFieldData = bugyoFieldsResponse.success
                ? bugyoFieldsResponse.data.find(
                    f => f.fieldName === bugyoField
                  )
                : null;

              return {
                collaboField: collaboFieldData?.name || collaboField, // Use current name if available
                bugyoField,
                collaboFieldType:
                  collaboFieldData?.convertType || FieldType.STRING,
                bugyoFieldType: bugyoFieldData?.fieldType || FieldType.STRING,
                isNew: false,
                isModified: Boolean(
                  collaboFieldData && collaboFieldData.name !== collaboField
                ), // Mark as modified if name changed
                isDeleted: false,
                originalCollaboField: collaboField,
                originalBugyoField: bugyoField,
              };
            })
            .filter(mapping => mapping !== null);
          setDetailMappings(detailMappings);

          // Convert dataListInfo mappings to UI format with correct field types
          const dataListInfoMappings: MappingField[] = Object.entries(
            mappingData.dataListInfoJson
          )
            .map(([collaboField, bugyoField], index) => {
              const collaboFieldData = collaboFieldsResponse.success
                ? findCollaboField(collaboField, "", collaboFieldsResponse.data)
                : null;

              // Check if field is missing
              if (!collaboFieldData) {
                const warningKey = `missing_datalist_${index}`;
                newMissingFieldWarnings[warningKey] =
                  `申請書項目「${collaboField}」が見つかりません。申請書のフォームが変更された可能性があります。`;
                return null; // Skip this mapping
              }

              const bugyoFieldData = bugyoFieldsResponse.success
                ? bugyoFieldsResponse.data.find(
                    f => f.fieldName === bugyoField
                  )
                : null;

              return {
                collaboField: collaboFieldData?.name || collaboField, // Use current name if available
                bugyoField,
                collaboFieldType:
                  collaboFieldData?.convertType || FieldType.STRING,
                bugyoFieldType: bugyoFieldData?.fieldType || FieldType.STRING,
                isNew: false,
                isModified: Boolean(
                  collaboFieldData && collaboFieldData.name !== collaboField
                ), // Mark as modified if name changed
                isDeleted: false,
                originalCollaboField: collaboField,
                originalBugyoField: bugyoField,
              };
            })
            .filter(mapping => mapping !== null);

          // Set missing field warnings
          setMissingFieldWarnings(newMissingFieldWarnings);

          // Set new mappings directly
          setHeaderMappings(headerMappings);
          setDetailMappings(detailMappings);
          setDataListInfoMappings(dataListInfoMappings);

          // Auto switch to appropriate tab based on data type for fallback
          if (mappingData.bugyoDataType === BugyoDataType.CUSTOMER_DATA) {
            setActiveTab("dataListInfo");
          } else {
            setActiveTab("header");
          }
        }

        // Do not save to cache when loading existing data from DB
        // Cache is only for user's draft changes
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error("Error loading mapping data:", error);
    }
  };

  const loadCollaboDataFields = async () => {
    try {
      setLoadingFields(true);
      const response = await mappingService.getCollaboDataFields(
        formData.collaboFormId
      );
      if (response.success) {
        setCollaboDataFields(response.data.fields);
        setCollaboDataTables(response.data.tables);
        // Note: We no longer clear mappings here as cache handles the mapping state
        // This function is now only responsible for loading field options
      }
    } catch (error) {
      console.error("Error loading collabo data fields:", error);
    } finally {
      setLoadingFields(false);
    }
  };

  const loadBugyoDataFields = async () => {
    try {
      setLoadingFields(true);
      const response = await mappingService.getBugyoDataFields(
        formData.bugyoDataType
      );
      if (response.success) {
        setBugyoDataFields(response.data);
        // Note: We no longer clear mappings here as cache handles the mapping state
        // This function is now only responsible for loading field options
      }
    } catch (error) {
      console.error("Error loading bugyo data fields:", error);
    } finally {
      setLoadingFields(false);
    }
  };

  const addMappingRow = (tab: string) => {
    try {
      const newMapping: MappingField = {
        collaboField: "",
        bugyoField: "",
        collaboFieldType: FieldType.STRING,
        bugyoFieldType: FieldType.STRING,
        isNew: true,
        isModified: false,
        isDeleted: false,
      };

      // Use functional updates to ensure we get the latest state
      if (tab === "header") {
        setHeaderMappings(prev => {
          const newMappings = [...prev, newMapping];
          return newMappings;
        });
      } else if (tab === "dataListInfo") {
        setDataListInfoMappings(prev => {
          const newMappings = [...prev, newMapping];
          return newMappings;
        });
      } else if (tab.startsWith("detail-table-")) {
        // Handle dynamic table tabs
        setTableMappings(prev => {
          const currentMappings = prev[tab] || [];
          return {
            ...prev,
            [tab]: [...currentMappings, newMapping],
          };
        });
      } else if (tab === "detail") {
        // Keep for backward compatibility
        setDetailMappings(prev => {
          const newMappings = [...prev, newMapping];
          return newMappings;
        });
      }

      setHasUnsavedChanges(true);

      // Auto-save to cache after state update
      setTimeout(() => {
        saveCurrentMappingsToCache();
      }, 0);

      // Clear any existing errors for the new row - use setTimeout to ensure state is updated
      setTimeout(() => {
        setErrors(prev => {
          const newErrors = { ...prev };
          // Calculate global index for the new row
          let globalIndex = 0;
          if (tab === "header") {
            globalIndex = headerMappings.length; // New row will be at the end of header mappings
          } else if (tab === "detail") {
            globalIndex = headerMappings.length + detailMappings.length; // New row will be at the end of detail mappings
          } else if (tab.startsWith("detail-table-")) {
            // For table tabs, calculate offset from previous tables
            globalIndex = headerMappings.length + detailMappings.length;
            const currentTabKeys = Object.keys(tableMappings);
            const currentTabIndex = currentTabKeys.indexOf(tab);
            for (let i = 0; i < currentTabIndex; i++) {
              globalIndex += (tableMappings[currentTabKeys[i]] || []).length;
            }
            globalIndex += (tableMappings[tab] || []).length; // Add current tab's length
          } else if (tab === "dataListInfo") {
            const tableMappingsLength = Object.values(tableMappings).reduce(
              (sum, maps) => sum + maps.length,
              0
            );
            globalIndex =
              headerMappings.length +
              detailMappings.length +
              tableMappingsLength +
              dataListInfoMappings.length; // New row will be at the end of dataListInfo mappings
          }

          delete newErrors[`mapping_${globalIndex}_collaboField`];
          delete newErrors[`mapping_${globalIndex}_bugyoField`];
          return newErrors;
        });
      }, 0);
    } catch (error) {
      console.error("Error in addMappingRow:", error);
    }
  };

  const removeMappingRow = (tab: string, index: number) => {
    const updateMappings = (prev: MappingField[]) => {
      const updated = [...prev];
      if (updated[index].isNew || !updated[index].id) {
        // If it's a new row (no real ID), remove it completely
        return updated.filter((_, i) => i !== index);
      } else {
        // If it's an existing row (has real ID), mark it as deleted for backend processing
        updated[index] = { ...updated[index], isDeleted: true };
        return updated;
      }
    };

    if (tab === "header") {
      setHeaderMappings(updateMappings);
    } else if (tab === "dataListInfo") {
      setDataListInfoMappings(updateMappings);
    } else if (tab.startsWith("detail-table-")) {
      setTableMappings(prev => ({
        ...prev,
        [tab]: updateMappings(prev[tab] || []),
      }));
    } else if (tab === "detail") {
      setDetailMappings(updateMappings);
    }
    setHasUnsavedChanges(true);

    // Auto-save to cache after state update
    setTimeout(() => {
      saveCurrentMappingsToCache();
    }, 0);
  };

  // Helper function to calculate global index for error messages
  const getGlobalIndex = (localIndex: number, tab: string): number => {
    if (tab === "header") {
      return localIndex;
    } else if (tab === "detail") {
      return headerMappings.length + localIndex;
    } else if (tab.startsWith("detail-table-")) {
      let offset = headerMappings.length + detailMappings.length;
      // Add offsets from previous table tabs
      const currentTabIndex = Object.keys(tableMappings).indexOf(tab);
      for (let i = 0; i < currentTabIndex; i++) {
        const prevTabKey = Object.keys(tableMappings)[i];
        offset += (tableMappings[prevTabKey] || []).length;
      }
      return offset + localIndex;
    } else if (tab === "dataListInfo") {
      const tableMappingsLength = Object.values(tableMappings).reduce(
        (sum, maps) => sum + maps.length,
        0
      );
      return (
        headerMappings.length +
        detailMappings.length +
        tableMappingsLength +
        localIndex
      );
    }
    return localIndex;
  };

  const updateMappingField = (
    tab: string,
    index: number,
    field: MappingFieldName,
    value: string
  ) => {
    const currentMappings =
      tab === "header"
        ? headerMappings
        : tab === "dataListInfo"
          ? dataListInfoMappings
          : tab.startsWith("detail-table-")
            ? tableMappings[tab] || []
            : detailMappings;
    const updatedMappings = [...currentMappings];
    const originalMapping = updatedMappings[index];

    // Update the field value
    updatedMappings[index] = {
      ...originalMapping,
      [field]: value,
    };

    // Check if this is a modification for existing records (not new records)
    if (!originalMapping.isNew) {
      const hasChangedFromOriginal =
        (field === MappingFieldName.COLLABO_FIELD &&
          value !== originalMapping.originalCollaboField) ||
        (field === MappingFieldName.BUGYO_FIELD &&
          value !== originalMapping.originalBugyoField) ||
        (field !== MappingFieldName.COLLABO_FIELD &&
          originalMapping.collaboField !==
            originalMapping.originalCollaboField) ||
        (field !== MappingFieldName.BUGYO_FIELD &&
          originalMapping.bugyoField !== originalMapping.originalBugyoField);

      updatedMappings[index].isModified = hasChangedFromOriginal;
    }

    // Update field types based on selection
    if (field === MappingFieldName.COLLABO_FIELD) {
      // Get the correct field list based on the current tab
      const availableFields = getAvailableCollaboFields(tab);
      const collaboField = availableFields.find(f => f.name === value);
      if (collaboField) {
        updatedMappings[index].collaboFieldType = collaboField.convertType;
      }
    } else if (field === MappingFieldName.BUGYO_FIELD) {
      const bugyoField = bugyoDataFields.find(f => f.fieldName === value);
      if (bugyoField) {
        updatedMappings[index].bugyoFieldType = bugyoField.fieldType;
      }
    }

    if (tab === "header") {
      setHeaderMappings(updatedMappings);
    } else if (tab === "dataListInfo") {
      setDataListInfoMappings(updatedMappings);
    } else if (tab.startsWith("detail-table-")) {
      setTableMappings(prev => ({
        ...prev,
        [tab]: updatedMappings,
      }));
    } else if (tab === "detail") {
      setDetailMappings(updatedMappings);
    }
    setHasUnsavedChanges(true);

    // Auto-save to cache after state update
    setTimeout(() => {
      saveCurrentMappingsToCache();
    }, 0);

    // Don't show validation errors during editing - only validate on Save
  };

  const handleMappingFieldBlur = (
    _tab: string,
    _index: number,
    _field: MappingFieldName
  ) => {
    // Don't validate on blur - only validate when Save is clicked
    // This allows users to change values without immediately seeing errors
  };

  // Helper function to validate required fields for a specific tab without changing activeTab
  const validateRequiredFieldsForTab = (
    tab: "header" | "detail" | "dataListInfo"
  ): { isValid: boolean; missingRequiredFields: string[] } => {
    const missingRequiredFields: string[] = [];

    // Get all required fields for the specified tab
    const dataInfoType =
      tab === "header"
        ? "HEADER"
        : tab === "detail"
          ? "DETAIL"
          : "DATA_LIST_INFO";

    const requiredFields = bugyoDataFields.filter(
      field => field.dataInfo === dataInfoType && field.isRequired
    );

    // Get mappings for the specified tab
    const tabMappings =
      tab === "header"
        ? headerMappings
        : tab === "detail"
          ? detailMappings
          : dataListInfoMappings;

    const activeMappings = tabMappings.filter(mapping => !mapping.isDeleted);
    const mappedBugyoFields = activeMappings.map(
      mapping => mapping.bugyoField
    );

    // Check which required fields are missing
    requiredFields.forEach(requiredField => {
      if (!mappedBugyoFields.includes(requiredField.fieldName)) {
        missingRequiredFields.push(requiredField.fieldName);
      }
    });

    return {
      isValid: missingRequiredFields.length === 0,
      missingRequiredFields,
    };
  };

  const handleCollaboDataTypeChange = (value: string) => {
    const id = parseInt(value);
    const selectedType = collaboDataTypes.find(type => type.id === id);

    // Don't do anything if the same value is selected
    if (id === formData.collaboFormId) {
      return;
    }

    // Clear ALL errors when user makes a selection, set error when selecting default
    if (id > 0) {
      // Clear all errors including mapping field errors
      setErrors({});
      // Clear all tab error states
      setTabErrors({
        header: false,
        dataListInfo: false,
      });
      // Clear all tab error messages
      setTabErrorMessages({
        header: [],
        dataListInfo: [],
      });
    } else {
      // User selected default option, only show this error
      setErrors({
        collaboDataType: "申請書を選択してください。",
      });
      // Clear tab errors
      setTabErrors({
        header: false,
        dataListInfo: false,
      });
      setTabErrorMessages({
        header: [],
        dataListInfo: [],
      });
    }

    // Save current mappings to cache before changing
    saveCurrentMappingsToCache();

    // Update form data
    setFormData(prev => ({
      ...prev,
      collaboFormId: id,
      collaboFormName: selectedType?.name || "",
    }));

    // Try to load mappings from cache for new combination
    if (id > 0 && formData.bugyoDataType) {
      const foundInCache = loadMappingsFromCache(id, formData.bugyoDataType);
      if (!foundInCache) {
        // No cache found, clear mappings
        setHeaderMappings([]);
        setDetailMappings([]);
        setDataListInfoMappings([]);
        setTableMappings({}); // Reset table mappings

        // Reset active tab based on bugyo data type
        if (formData.bugyoDataType === BugyoDataType.CUSTOMER_DATA) {
          setActiveTab("dataListInfo");
        } else {
          setActiveTab("header");
        }
      }
    } else {
      // Clear mappings if no valid selection
      setHeaderMappings([]);
      setDetailMappings([]);
      setDataListInfoMappings([]);
      setTableMappings({}); // Reset table mappings
      setActiveTab("header");
    }

    setHasUnsavedChanges(true);
  };

  const handleBugyoDataTypeChange = (value: string) => {
    // Don't do anything if the same value is selected
    if (value === formData.bugyoDataType) {
      return;
    }

    // Clear ALL errors when user makes a selection, set error when selecting default
    if (value) {
      // Clear all errors including mapping field errors
      setErrors({});
      // Clear all tab error states
      setTabErrors({
        header: false,
        dataListInfo: false,
      });
      // Clear all tab error messages
      setTabErrorMessages({
        header: [],
        dataListInfo: [],
      });
    } else {
      // User selected default option, only show this error
      setErrors({
        bugyoDataType: "データ種別を選択してください。",
      });
      // Clear tab errors
      setTabErrors({
        header: false,
        dataListInfo: false,
      });
      setTabErrorMessages({
        header: [],
        dataListInfo: [],
      });
    }

    // Save current mappings to cache before changing
    saveCurrentMappingsToCache();

    // Update form data
    setFormData(prev => ({
      ...prev,
      bugyoDataType: value,
    }));

    // Try to load mappings from cache for new combination
    if (value && formData.collaboFormId) {
      const foundInCache = loadMappingsFromCache(formData.collaboFormId, value);
      if (!foundInCache) {
        // No cache found, clear mappings
        setHeaderMappings([]);
        setDetailMappings([]);
        setDataListInfoMappings([]);

        // Initialize empty mappings for each table
        const newTableMappings: Record<string, MappingField[]> = {};
        collaboDataTables.forEach(table => {
          const tabKey = `detail-table-${table.key}`;
          newTableMappings[tabKey] = [];
        });
        setTableMappings(newTableMappings);

        // Auto switch to dataListInfo tab for CUSTOMER_DATA
        if (value === BugyoDataType.CUSTOMER_DATA) {
          setActiveTab("dataListInfo");
        } else {
          setActiveTab("header");
        }
      }
    } else {
      // Clear mappings if no valid selection
      setHeaderMappings([]);
      setDetailMappings([]);
      setDataListInfoMappings([]);
      setTableMappings({}); // Reset table mappings
      setActiveTab("header");
    }

    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    // Clear previous errors
    setErrors({});

    // Collect validation errors for individual fields
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    // Basic form validation
    if (!formData.collaboFormId) {
      newErrors.collaboDataType = "申請書を選択してください。";
      hasErrors = true;
    }

    if (!formData.bugyoDataType) {
      newErrors.bugyoDataType = "データ種別を選択してください。";
      hasErrors = true;
    }

    // Check for tables if not CUSTOMER_DATA
    if (
      formData.collaboFormId &&
      formData.bugyoDataType &&
      formData.bugyoDataType !== BugyoDataType.CUSTOMER_DATA
    ) {
      if (!collaboDataTables || collaboDataTables.length === 0) {
        newErrors.noTables = "申請書に明細項目が存在しないため、登録できません";
        hasErrors = true;
      }
    }

    // Only validate mappings if both form and data type are selected
    if (formData.collaboFormId && formData.bugyoDataType) {
      // Validate header mappings
      headerMappings.forEach((mapping, index) => {
        if (mapping.isDeleted) return;

        if (!mapping.collaboField) {
          newErrors[`mapping_${index}_collaboField`] =
            "申請書項目を選択してください。";
          hasErrors = true;
        }

        if (!mapping.bugyoField) {
          newErrors[`mapping_${index}_bugyoField`] =
            "商奉行API項目を選択してください。";
          hasErrors = true;
        }
      });

      // Validate detail mappings (for backward compatibility)
      if (detailMappings.length > 0) {
        detailMappings.forEach((mapping, index) => {
          if (mapping.isDeleted) return;

          const globalIndex = headerMappings.length + index;

          if (!mapping.collaboField) {
            newErrors[`mapping_${globalIndex}_collaboField`] =
              "申請書項目を選択してください。";
            hasErrors = true;
          }

          if (!mapping.bugyoField) {
            newErrors[`mapping_${globalIndex}_bugyoField`] =
              "商奉行API項目を選択してください。";
            hasErrors = true;
          }
        });
      }

      // Validate table mappings
      let tableOffset = headerMappings.length + detailMappings.length;
      Object.entries(tableMappings).forEach(([_tabKey, mappings]) => {
        mappings.forEach((mapping, index) => {
          if (mapping.isDeleted) return;

          const globalIndex = tableOffset + index;

          if (!mapping.collaboField) {
            newErrors[`mapping_${globalIndex}_collaboField`] =
              "申請書項目を選択してください。";
            hasErrors = true;
          }

          if (!mapping.bugyoField) {
            newErrors[`mapping_${globalIndex}_bugyoField`] =
              "商奉行API項目を選択してください。";
            hasErrors = true;
          }
        });
        tableOffset += mappings.length;
      });

      // Validate dataListInfo mappings
      dataListInfoMappings.forEach((mapping, index) => {
        if (mapping.isDeleted) return;

        const globalIndex =
          headerMappings.length +
          detailMappings.length +
          Object.values(tableMappings).reduce(
            (sum, maps) => sum + maps.length,
            0
          ) +
          index;

        if (!mapping.collaboField) {
          newErrors[`mapping_${globalIndex}_collaboField`] =
            "申請書項目を選択してください。";
          hasErrors = true;
        }

        if (!mapping.bugyoField) {
          newErrors[`mapping_${globalIndex}_bugyoField`] =
            "商奉行API項目を選択してください。";
          hasErrors = true;
        }
      });

      // Check if there are mappings in all required tabs and update tab error state
      const newTabErrors: Record<string, boolean> = {
        header: false,
        dataListInfo: false,
      };

      const newTabErrorMessages: Record<string, string[]> = {
        header: [],
        dataListInfo: [],
      };

      // Initialize table tabs in newTabErrors and newTabErrorMessages
      collaboDataTables.forEach(table => {
        const tabKey = `detail-table-${table.key}`;
        newTabErrors[tabKey] = false;
        newTabErrorMessages[tabKey] = [];
      });

      // Validate required fields for each tab
      if (formData.bugyoDataType === BugyoDataType.CUSTOMER_DATA) {
        // Validate dataListInfo tab only
        const validation = validateRequiredFieldsForTab("dataListInfo");
        if (!validation.isValid) {
          newTabErrorMessages.dataListInfo.push(
            `必須項目がマッピングされていません: ${validation.missingRequiredFields.join(", ")}`
          );
          newTabErrors.dataListInfo = true;
          hasErrors = true;
        }
      } else {
        // Validate header tab
        const headerValidation = validateRequiredFieldsForTab("header");
        if (!headerValidation.isValid) {
          newTabErrorMessages.header.push(
            `必須項目がマッピングされていません: ${headerValidation.missingRequiredFields.join(", ")}`
          );
          newTabErrors.header = true;
          hasErrors = true;
        }

        // Check if at least one table has mappings
        const hasAnyTableMapping = collaboDataTables.some(table => {
          const tabKey = `detail-table-${table.key}`;
          const tabMappings = tableMappings[tabKey] || [];
          const activeMappings = tabMappings.filter(m => !m.isDeleted);
          return activeMappings.length > 0;
        });

        // If no tables have any mappings, show error
        if (!hasAnyTableMapping) {
          // Mark all table tabs with error state (red border)
          // But only show the error message on the first table tab
          collaboDataTables.forEach((table, index) => {
            const tabKey = `detail-table-${table.key}`;
            // Only add error message to the first table tab
            if (index === 0) {
              newTabErrorMessages[tabKey].push(
                "少なくとも1つのテーブルにマッピングを追加してください"
              );
            }
            // Mark all tabs with error state
            newTabErrors[tabKey] = true;
          });
          hasErrors = true;
        } else {
          // Validate each table tab that has mappings
          collaboDataTables.forEach(table => {
            const tabKey = `detail-table-${table.key}`;
            const tabMappings = tableMappings[tabKey] || [];
            const activeMappings = tabMappings.filter(m => !m.isDeleted);

            // Only validate tables that have mappings
            if (activeMappings.length > 0) {
              // Check for type mismatch errors in table tabs
              const hasTypeMismatch = activeMappings.some(mapping =>
                getTypeMismatchError(mapping)
              );
              if (hasTypeMismatch) {
                newTabErrorMessages[tabKey].push(
                  "データ型が一致しない項目があります"
                );
                newTabErrors[tabKey] = true;
                hasErrors = true;
              }

              // Check for field-level errors (empty fields)
              const fieldErrorCount = Object.keys(newErrors).filter(
                key =>
                  key.startsWith(`mapping_`) &&
                  tabMappings.some((_, idx) => {
                    const globalIdx = getGlobalIndex(idx, tabKey);
                    return (
                      key === `mapping_${globalIdx}_collaboField` ||
                      key === `mapping_${globalIdx}_bugyoField`
                    );
                  })
              ).length;
              if (fieldErrorCount > 0) {
                newTabErrorMessages[tabKey].push(
                  `${fieldErrorCount}個の項目が未入力です`
                );
                newTabErrors[tabKey] = true;
              }
            }
          });
        }
      }

      // Helper function to check for type mismatch errors in each tab
      const checkTabForTypeMismatch = (
        tab: "header" | "detail" | "dataListInfo"
      ) => {
        const tabMappings =
          tab === "header"
            ? headerMappings
            : tab === "detail"
              ? detailMappings
              : dataListInfoMappings;

        return tabMappings.some(
          mapping => !mapping.isDeleted && getTypeMismatchError(mapping)
        );
      };

      // Additional validation for CUSTOMER_DATA
      if (formData.bugyoDataType === BugyoDataType.CUSTOMER_DATA) {
        const dataListInfoActiveMappings = dataListInfoMappings.filter(
          mapping => !mapping.isDeleted
        );
        if (dataListInfoActiveMappings.length === 0) {
          newErrors.dataListInfoMapping =
            "データリスト情報タブにマッピングを追加してください。";
          newTabErrorMessages.dataListInfo.push("マッピングを追加してください");
          newTabErrors.dataListInfo = true;
          hasErrors = true;
        }
        // Check for type mismatch errors
        if (checkTabForTypeMismatch("dataListInfo")) {
          newTabErrorMessages.dataListInfo.push(
            "データ型が一致しない項目があります"
          );
          newTabErrors.dataListInfo = true;
          hasErrors = true;
        }

        // Check for field-level errors in dataListInfo
        const dataListInfoFieldErrorCount = Object.keys(newErrors).filter(
          key =>
            key.startsWith(`mapping_`) &&
            dataListInfoMappings.some((_, idx) => {
              const globalIdx = getGlobalIndex(idx, "dataListInfo");
              return (
                key === `mapping_${globalIdx}_collaboField` ||
                key === `mapping_${globalIdx}_bugyoField`
              );
            })
        ).length;
        if (dataListInfoFieldErrorCount > 0) {
          newTabErrorMessages.dataListInfo.push(
            `${dataListInfoFieldErrorCount}個の項目が未入力です`
          );
          newTabErrors.dataListInfo = true;
        }
      } else {
        // Additional validation for non-CUSTOMER_DATA types
        const headerActiveMappings = headerMappings.filter(
          mapping => !mapping.isDeleted
        );

        if (headerActiveMappings.length === 0) {
          newErrors.headerMapping =
            "ヘッダー情報タブにマッピングを追加してください。";
          newTabErrorMessages.header.push("マッピングを追加してください");
          newTabErrors.header = true;
          hasErrors = true;
        }
        // Check for type mismatch errors in header
        if (checkTabForTypeMismatch("header")) {
          newTabErrorMessages.header.push("データ型が一致しない項目があります");
          newTabErrors.header = true;
          hasErrors = true;
        }

        // Check for field-level errors in header
        const headerFieldErrorCount = Object.keys(newErrors).filter(
          key =>
            key.startsWith(`mapping_`) &&
            headerMappings.some((_, idx) => {
              const globalIdx = getGlobalIndex(idx, "header");
              return (
                key === `mapping_${globalIdx}_collaboField` ||
                key === `mapping_${globalIdx}_bugyoField`
              );
            })
        ).length;
        if (headerFieldErrorCount > 0) {
          newTabErrorMessages.header.push(
            `${headerFieldErrorCount}個の項目が未入力です`
          );
          newTabErrors.header = true;
        }
      }

      // Update tab error state and messages
      setTabErrors(newTabErrors);
      setTabErrorMessages(newTabErrorMessages);
    }

    // Set errors and return if there are validation errors
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);

      // Save current mappings to cache before processing
      saveCurrentMappingsToCache();

      // Convert mappings to the new format with row numbers and categorize them
      const allMappings = [
        ...headerMappings.map((mapping, index) => ({
          ...mapping,
          dataInfo: "HEADER" as const,
          rowNumber: index + 1,
          tableKey: undefined,
        })),
        ...detailMappings.map((mapping, index) => ({
          ...mapping,
          dataInfo: "DETAIL" as const,
          rowNumber: index + 1,
          tableKey: undefined,
        })),
        ...dataListInfoMappings.map((mapping, index) => ({
          ...mapping,
          dataInfo: "DATA_LIST_INFO" as const,
          rowNumber: index + 1,
          tableKey: undefined,
        })),
        // Add table mappings with their table keys
        ...Object.entries(tableMappings).flatMap(([tabKey, mappings]) => {
          // Extract table key from tab key (format: "detail-table-{tableKey}")
          const tableKey = tabKey.replace("detail-table-", "");
          return mappings.map((mapping, index) => ({
            ...mapping,
            dataInfo: "DETAIL" as const,
            rowNumber: index + 1,
            tableKey: tableKey,
          }));
        }),
      ];

      // Filter and categorize mappings based on their state
      const activeMappings = allMappings.filter(mapping => !mapping.isDeleted);
      const deletedMappings = allMappings.filter(
        mapping => mapping.isDeleted && mapping.id
      );

      // Get bugyo field IDs for active mappings
      const getFieldMappingItem = (mapping: any): FieldMappingItem => {
        const bugyoField = bugyoDataFields.find(
          f => f.fieldName === mapping.bugyoField
        );

        // Find collabo field in all possible locations (fields + tables)
        let collaboField: CollaboDataField | null = null;
        if (mapping.tableKey) {
          // Find in table fields
          const table = collaboDataTables.find(t => t.key === mapping.tableKey);
          if (table) {
            collaboField =
              table.fields.find(f => f.name === mapping.collaboField) || null;
          }
        } else {
          // Find in direct fields
          collaboField =
            collaboDataFields.find(f => f.name === mapping.collaboField) ||
            null;
        }

        return {
          rowNumber: mapping.rowNumber,
          collaboFieldName: mapping.collaboField,
          collaboFieldKey: collaboField?.key || "",
          bugyoFieldName: mapping.bugyoField,
          bugyoFieldId: bugyoField?.id || 0,
          fieldType: mapping.collaboFieldType,
          dataInfo: mapping.dataInfo,
          tableKey: mapping.tableKey,
        };
      };

      const getFieldMappingUpdateItem = (
        mapping: any
      ): FieldMappingUpdateItem => {
        const bugyoField = bugyoDataFields.find(
          f => f.fieldName === mapping.bugyoField
        );

        // Find collabo field in all possible locations (fields + tables)
        let collaboField: CollaboDataField | null = null;
        if (mapping.tableKey) {
          // Find in table fields
          const table = collaboDataTables.find(t => t.key === mapping.tableKey);
          if (table) {
            collaboField =
              table.fields.find(f => f.name === mapping.collaboField) || null;
          }
        } else {
          // Find in direct fields
          collaboField =
            collaboDataFields.find(f => f.name === mapping.collaboField) ||
            null;
        }

        return {
          id: mapping.id!,
          rowNumber: mapping.rowNumber,
          collaboFieldName: mapping.collaboField,
          collaboFieldKey: collaboField?.key || "",
          bugyoFieldName: mapping.bugyoField,
          bugyoFieldId: bugyoField?.id || 0,
          fieldType: mapping.collaboFieldType,
          dataInfo: mapping.dataInfo,
          tableKey: mapping.tableKey,
        };
      };

      // Get all existing mapping IDs from the original form data (from API)
      const originalMappingIds = formData.fieldMappings?.map(fm => fm.id) || [];

      // Categorize mappings according to the update logic requirements:
      // 1. New mappings: User added new rows (isNew === true)
      //    → Send to newFieldMappingList for creation
      const newFieldMappingList: FieldMappingItem[] = activeMappings
        .filter(
          mapping =>
            mapping.isNew &&
            !mapping.id &&
            mapping.collaboField &&
            mapping.bugyoField
        )
        .map(getFieldMappingItem);

      // 2. Updated mappings: User modified existing rows (isModified === true, has real ID)
      //    → Send to updateFieldMappingList for updating
      const updateFieldMappingList: FieldMappingUpdateItem[] = activeMappings
        .filter(
          mapping =>
            mapping.isModified &&
            !mapping.isNew &&
            mapping.id &&
            mapping.collaboField &&
            mapping.bugyoField
        )
        .map(getFieldMappingUpdateItem);

      // 3. Deleted mappings: User deleted existing rows (isDeleted === true, has real ID)
      //    → Send IDs to deleteFieldMappingId for deletion
      const userDeletedMappings = deletedMappings
        .map(mapping => mapping.id!)
        .filter((id): id is number => Number.isFinite(id));

      // 4. Mappings that exist in original data but not in current active mappings
      //    → These should be deleted when form type changes
      const currentActiveMappingIds = activeMappings
        .filter(mapping => mapping.id)
        .map(mapping => mapping.id!);

      const typeChangeDeletedIds = originalMappingIds
        .filter(id => !currentActiveMappingIds.includes(id))
        .filter((id): id is number => Number.isFinite(id));

      // Combine user deleted mappings and type change deleted mappings
      const deleteFieldMappingId: number[] = [
        ...userDeletedMappings,
        ...typeChangeDeletedIds,
      ];

      const submitData: MappingRegistrationRequest = {
        collaboFormName: formData.collaboFormName,
        collaboFormId: formData.collaboFormId,
        bugyoDataType: formData.bugyoDataType as BugyoDataType,
        newFieldMappingList:
          newFieldMappingList.length > 0 ? newFieldMappingList : undefined,
        updateFieldMappingList:
          updateFieldMappingList.length > 0
            ? updateFieldMappingList
            : undefined,
        deleteFieldMappingId:
          deleteFieldMappingId.length > 0 ? deleteFieldMappingId : undefined,
      };

      if (isEdit) {
        await mappingService.updateMappingRegistration(
          parseInt(id!),
          submitData
        );
      } else {
        await mappingService.createMappingRegistration(submitData);
      }

      // Clear cache after successful save
      clearMappingCache();
      setHasUnsavedChanges(false);
      navigate("/mapping_registration_list");
    } catch (error) {
      console.error("Save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeMismatchError = (mapping: MappingField): boolean => {
    // Only compare types if both fields are selected
    if (!mapping.collaboField || !mapping.bugyoField) {
      return false;
    }
    return mapping.collaboFieldType !== mapping.bugyoFieldType;
  };

  const canAddMoreMappings = (): boolean => {
    // Always allow adding new rows regardless of field availability
    // Users can now map the same collabo field multiple times
    return true;
  };

  const canSave = (): boolean => {
    // Disable save button if loading, loadingFields, or if there's a noTables error
    return !loading && !loadingFields && !errors.noTables;
  };

  const handleInvalidSettingsConfirm = () => {
    setShowInvalidSettingsWarning(false);
    navigate("/settings");
  };

  const handleInvalidSettingsCancel = () => {
    setShowInvalidSettingsWarning(false);
    navigate("/mapping_registration_list");
  };

  const currentMappings =
    activeTab === "header"
      ? headerMappings
      : activeTab === "dataListInfo"
        ? dataListInfoMappings
        : activeTab.startsWith("detail-table-")
          ? tableMappings[activeTab] || []
          : detailMappings; // fallback for backward compatibility

  // Error handling - must be after all hooks
  if (hasError) {
    return (
      <div className="mapping-form-container">
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h2>エラーが発生しました</h2>
          <p>{errorMessage}</p>
          <button
            onClick={() => {
              setHasError(false);
              setErrorMessage("");
              window.location.reload();
            }}
            style={{
              padding: "8px 16px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            ページを再読み込み
          </button>
        </div>
      </div>
    );
  }

  // Safety check
  if (!currentMappings) {
    console.error("currentMappings is undefined or null");
    return (
      <div className="mapping-form-container">
        <Loading message="データを読み込み中..." fullHeight={true} />
      </div>
    );
  }

  // Show loading for data loading
  if (dataLoading) {
    return (
      <div className="mapping-form-container">
        <Loading message="データを読み込み中..." fullHeight={true} />
      </div>
    );
  }

  // Show loading for other operations
  if (loading && !hasUnsavedChanges) {
    return (
      <div className="mapping-form-container">
        <Loading message="データを読み込み中..." fullHeight={true} />
      </div>
    );
  }

  try {
    return (
      <div className="mapping-form-container">
        {/* Header */}
        <Header
          user={user}
          onLogout={onLogout}
          onLogoClick={handleLogoClick}
          onLogoutClick={() => handleLogoutClick(onLogout)}
        />
        {/* Back Button (aligned with content) */}
        <BackNav
          label="申請書マッピング一覧に戻る"
          to="/mapping_registration_list"
          hasUnsavedChanges={hasUnsavedChanges}
        />

        {/* Form Sections */}
        <div className="form-sections">
          {/* Page Title */}
          <div className="page-title-section">
            <h1 className="page-title">申請書マッピング設定</h1>
            <p className="page-description">
              申請書の項目と商奉行API項目の関連付けを設定します
            </p>
          </div>

          {/* Application Selection */}
          <div className="form-section dark-section">
            <h2 className="section-title">申請書選択</h2>
            <div className="form-group">
              <label className="form-label">申請書</label>
              <SearchableSelect
                value={formData.collaboFormId?.toString() || ""}
                onChange={value => handleCollaboDataTypeChange(value)}
                options={collaboDataTypes.map(type => ({
                  value: type.id?.toString() || "",
                  label: type.isUsed
                    ? `${type.name} (使用済み)`
                    : type.name || "",
                  disabled: type.isUsed,
                  tooltip: type.isUsed
                    ? "このフォームはすでに他のマッピング設定で使用されています"
                    : undefined,
                }))}
                placeholder="オプションを選択してください"
                searchPlaceholder="検索キーワードを入力"
                className="form-select"
                error={!!errors.collaboDataType}
                maxLength={255}
              />
              {errors.collaboDataType && (
                <span className="error-message">{errors.collaboDataType}</span>
              )}
              <p className="help-text">
                申請書を選択すると、その申請書の項目が以下のマッピングエリアに表示されます
              </p>
            </div>
          </div>

          {/* Bugyo Data Type Selection */}
          <div className="form-section dark-section">
            <h2 className="section-title">商奉行APIデータ種別選択</h2>

            <div className="form-group">
              <label className="form-label">データ種別</label>
              <SearchableSelect
                value={formData.bugyoDataType}
                onChange={value => handleBugyoDataTypeChange(value)}
                options={bugyoDataTypes.map(type => ({
                  value: type || "",
                  label: BugyoDataTypeLabels[type] || "",
                }))}
                placeholder="オプションを選択してください"
                searchPlaceholder="検索キーワードを入力"
                className="form-select"
                error={!!errors.bugyoDataType}
                maxLength={255}
              />
              {errors.bugyoDataType && (
                <span className="error-message">{errors.bugyoDataType}</span>
              )}
              <p className="help-text">
                データ種別を選択すると、そのデータ種別のAPI項目が以下のマッピングエリアに表示されます
              </p>
            </div>
          </div>

          {/* Webhook URL Section - Edit mode only */}
          {isEdit && (
            <div className="form-section dark-section">
              <h2 className="section-title">Webhook URL</h2>
              <div className="form-group">
                <label className="form-label">コラボフローに設定するWebhook URL</label>
                {webhookToken ? (
                  <>
                    <div className="webhook-url-display">
                      <input
                        type="text"
                        readOnly
                        value={getWebhookUrl(webhookToken)}
                        className="webhook-url-input"
                        onClick={e => (e.target as HTMLInputElement).select()}
                      />
                    </div>
                    <div className="webhook-url-buttons">
                      <button
                        type="button"
                        onClick={handleCopyWebhookUrl}
                        className="webhook-url-btn copy-btn"
                      >
                        {webhookCopied ? "✓ コピー済" : "コピー"}
                      </button>
                      <button
                        type="button"
                        onClick={handleRegenerateWebhookToken}
                        disabled={regeneratingToken}
                        className="webhook-url-btn generate-btn"
                      >
                        {regeneratingToken ? "生成中..." : "再生成"}
                      </button>
                    </div>
                    <p className="help-text">
                      このURLをコラボフローのWebhook通知先に設定してください。再生成すると以前のURLは無効になります。
                    </p>
                  </>
                ) : (
                  <>
                    <div className="webhook-url-display">
                      <input
                        type="text"
                        readOnly
                        value=""
                        placeholder="Webhook URLが未生成です"
                        className="webhook-url-input"
                      />
                    </div>
                    <div className="webhook-url-buttons">
                      <button
                        type="button"
                        onClick={handleRegenerateWebhookToken}
                        disabled={regeneratingToken}
                        className="webhook-url-btn generate-btn"
                      >
                        {regeneratingToken ? "生成中..." : "URLを生成"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Mapping Area - Only show when both types are selected and no table error */}
          {formData.collaboFormId &&
          formData.bugyoDataType &&
          !errors.noTables ? (
            <div className="form-section">
              {loadingFields ? (
                <div
                  className="empty-mapping"
                  style={{ padding: "4rem 2rem", minHeight: "300px" }}
                >
                  <div className="loading-spinner"></div>
                  <p>フィールドデータを読み込み中...</p>
                </div>
              ) : (
                <>
                  <div className="mapping-tabs">
                    {formData.bugyoDataType !==
                      BugyoDataType.CUSTOMER_DATA && (
                      <>
                        <button
                          className={`tab-button ${activeTab === "header" ? "active" : ""} ${tabErrors.header ? "error" : ""}`}
                          onClick={() => setActiveTab("header")}
                        >
                          ヘッダー情報
                        </button>
                        {(() => {
                          const tablesToShow = isEdit
                            ? (() => {
                                // If data is still loading, show all tables temporarily to avoid flicker
                                if (dataLoading) {
                                  return collaboDataTables;
                                }

                                // Show only tables that have mappings
                                const tableMappingKeys = Object.keys(
                                  tableMappings
                                ).filter(key =>
                                  key.startsWith("detail-table-")
                                );

                                const result = tableMappingKeys
                                  .map(tabKey => {
                                    const tableKey = tabKey.replace(
                                      "detail-table-",
                                      ""
                                    );
                                    const found = collaboDataTables.find(
                                      t => t.key === tableKey
                                    );
                                    return found;
                                  })
                                  .filter(
                                    (table): table is CollaboTable =>
                                      table !== undefined
                                  );

                                return result;
                              })()
                            : collaboDataTables;

                          return tablesToShow.map((table, index) => {
                            const tabKey = `detail-table-${table.key}`;
                            return (
                              <button
                                key={table.key}
                                className={`tab-button ${activeTab === tabKey ? "active" : ""} ${tabErrors[tabKey] ? "error" : ""}`}
                                onClick={() => setActiveTab(tabKey)}
                              >
                                明細情報 {index + 1}
                              </button>
                            );
                          });
                        })()}
                      </>
                    )}
                    {formData.bugyoDataType ===
                      BugyoDataType.CUSTOMER_DATA && (
                      <button
                        className={`tab-button ${activeTab === "dataListInfo" ? "active" : ""} ${tabErrors.dataListInfo ? "error" : ""}`}
                        onClick={() => setActiveTab("dataListInfo")}
                      >
                        データリスト情報
                      </button>
                    )}
                  </div>

                  <div className="tab-description">
                    {activeTab === "header"
                      ? "申請書のヘッダー項目と商奉行API項目との紐づけを設定します"
                      : activeTab === "dataListInfo"
                        ? "申請書の得意先項目と商奉行API項目との紐づけを設定します"
                        : activeTab.startsWith("detail-table-")
                          ? "申請書の明細項目と商奉行API項目の関連付けを設定します"
                          : "申請書の明細項目と商奉行API項目の関連付けを設定します"}
                  </div>

                  <div className="mapping-table-container">
                    {currentMappings.length === 0 ? (
                      <div className="empty-mapping">
                        <p>データがありません</p>
                      </div>
                    ) : (
                      <table className="mapping-table">
                        <thead>
                          <tr>
                            <th className="no-column">No.</th>
                            <th className="collabo-column">申請書項目</th>
                            <th className="arrow-column"></th>
                            <th className="bugyou-column">
                              商奉行API項目
                            </th>
                            <th className="action-column">操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentMappings &&
                            currentMappings
                              .map((mapping, originalIndex) => ({
                                mapping,
                                originalIndex,
                                globalIndex: getGlobalIndex(
                                  originalIndex,
                                  activeTab
                                ),
                              }))
                              .filter(({ mapping }) => !mapping.isDeleted) // Hide deleted rows
                              .map(
                                (
                                  { mapping, originalIndex, globalIndex },
                                  displayIndex
                                ) => (
                                  <tr
                                    key={originalIndex}
                                    className={`mapping-row ${getTypeMismatchError(mapping) ? "type-mismatch" : ""}`}
                                  >
                                    <td className="no-cell">
                                      {displayIndex + 1}
                                    </td>
                                    <td className="collabo-cell">
                                      <SearchableSelect
                                        value={mapping.collaboField}
                                        onChange={value =>
                                          updateMappingField(
                                            activeTab,
                                            originalIndex,
                                            MappingFieldName.COLLABO_FIELD,
                                            value
                                          )
                                        }
                                        onBlur={() =>
                                          handleMappingFieldBlur(
                                            activeTab,
                                            originalIndex,
                                            MappingFieldName.COLLABO_FIELD
                                          )
                                        }
                                        options={getAvailableCollaboFields(
                                          activeTab
                                        ).map(field => ({
                                          value: field.name || "",
                                          label: field.name || "",
                                        }))}
                                        placeholder="オプションを選択してください"
                                        searchPlaceholder="検索キーワードを入力"
                                        className="mapping-select"
                                        error={
                                          !!errors[
                                            `mapping_${globalIndex}_collaboField`
                                          ]
                                        }
                                        disabled={false}
                                        maxLength={255}
                                      />
                                      {errors[
                                        `mapping_${globalIndex}_collaboField`
                                      ] && (
                                        <div className="mapping-error-message">
                                          {
                                            errors[
                                              `mapping_${globalIndex}_collaboField`
                                            ]
                                          }
                                        </div>
                                      )}
                                    </td>
                                    <td className="arrow-cell">
                                      <div className="arrow-container">
                                        <span className="arrow-symbol">→</span>
                                        {getTypeMismatchError(mapping) && (
                                          <div
                                            className="type-mismatch-warning"
                                            data-tooltip="フィールドタイプが一致しません"
                                          >
                                            ⚠️
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="bugyo-cell">
                                      <SearchableSelect
                                        value={mapping.bugyoField}
                                        onChange={value =>
                                          updateMappingField(
                                            activeTab,
                                            originalIndex,
                                            MappingFieldName.BUGYO_FIELD,
                                            value
                                          )
                                        }
                                        onBlur={() =>
                                          handleMappingFieldBlur(
                                            activeTab,
                                            originalIndex,
                                            MappingFieldName.BUGYO_FIELD
                                          )
                                        }
                                        options={getAvailableBugyoFields(
                                          activeTab,
                                          mapping
                                        ).map(field => ({
                                          value: field.fieldName || "",
                                          label: field.fieldName || "",
                                          isRequired: field.isRequired,
                                        }))}
                                        placeholder="オプションを選択してください"
                                        searchPlaceholder="検索キーワードを入力"
                                        className="mapping-select"
                                        error={
                                          !!errors[
                                            `mapping_${globalIndex}_bugyoField`
                                          ]
                                        }
                                        disabled={false}
                                        maxLength={255}
                                      />
                                      {errors[
                                        `mapping_${globalIndex}_bugyoField`
                                      ] && (
                                        <div className="mapping-error-message">
                                          {
                                            errors[
                                              `mapping_${globalIndex}_bugyoField`
                                            ]
                                          }
                                        </div>
                                      )}
                                    </td>
                                    <td className="action-cell">
                                      <button
                                        className="delete-button"
                                        onClick={() =>
                                          removeMappingRow(
                                            activeTab,
                                            originalIndex
                                          )
                                        }
                                      >
                                        削除
                                      </button>
                                    </td>
                                  </tr>
                                )
                              )}
                        </tbody>
                      </table>
                    )}

                    {canAddMoreMappings() && (
                      <div className="mapping-actions">
                        <button
                          className="add-row-button"
                          onClick={() => addMappingRow(activeTab)}
                        >
                          行を追加
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Missing Field Warnings */}
                  {Object.keys(missingFieldWarnings).length > 0 && (
                    <div
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: "4px",
                        padding: "12px 16px",
                        marginBottom: "16px",
                        color: "#856404",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                        }}
                      >
                        <span style={{ fontSize: "20px" }}>⚠️</span>
                        <div style={{ flex: 1 }}>
                          <strong
                            style={{ display: "block", marginBottom: "8px" }}
                          >
                            申請書フォームが変更されました
                          </strong>
                          {Object.values(missingFieldWarnings).map(
                            (warning, index) => (
                              <div key={index} style={{ marginBottom: "4px" }}>
                                • {warning}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mapping validation errors - show all errors at the bottom */}
                  {formData.collaboFormId && formData.bugyoDataType && (
                    <div className="validation-errors-container">
                      {/* Tab-specific errors */}
                      {formData.bugyoDataType ===
                      BugyoDataType.CUSTOMER_DATA ? (
                        // For CUSTOMER_DATA, only show dataListInfo tab
                        tabErrorMessages.dataListInfo.length > 0 && (
                          <div className="tab-error-section">
                            <div className="tab-error-title">
                              データリスト情報タブ
                            </div>
                            <div className="tab-error-messages">
                              {tabErrorMessages.dataListInfo.map(
                                (message, index) => (
                                  <div
                                    key={index}
                                    className="tab-error-message"
                                  >
                                    {message}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )
                      ) : (
                        // For other data types, show header and table tabs
                        <>
                          {/* Header tab errors */}
                          {tabErrorMessages.header.length > 0 && (
                            <div className="tab-error-section">
                              <div className="tab-error-title">
                                ヘッダー情報タブ
                              </div>
                              <div className="tab-error-messages">
                                {tabErrorMessages.header.map(
                                  (message, index) => (
                                    <div
                                      key={index}
                                      className="tab-error-message"
                                    >
                                      {message}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                          {/* Table tab errors */}
                          {collaboDataTables.map((table, index) => {
                            const tabKey = `detail-table-${table.key}`;
                            const messages = tabErrorMessages[tabKey];

                            if (!messages || messages.length === 0) return null;

                            return (
                              <div key={tabKey} className="tab-error-section">
                                <div className="tab-error-title">
                                  明細情報 {index + 1}
                                </div>
                                <div className="tab-error-messages">
                                  {messages.map((message, index) => (
                                    <div
                                      key={index}
                                      className="tab-error-message"
                                    >
                                      {message}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="form-section">
              <div className="mapping-placeholder">
                <div className="placeholder-content">
                  {loadingFields ? (
                    <>
                      <div className="loading-spinner"></div>
                      <p>フィールドデータを読み込み中...</p>
                    </>
                  ) : errors.noTables ? (
                    <>
                      <h3 style={{ color: "#dc3545" }}>⚠️ 登録できません</h3>
                      <p style={{ color: "#dc3545", fontWeight: "500" }}>
                        {errors.noTables}
                      </p>
                    </>
                  ) : (
                    <>
                      <h3>マッピング設定</h3>
                      <p>
                        申請書とデータ種別を選択すると、マッピング設定が表示されます。
                      </p>
                      <div className="placeholder-steps">
                        <div className="step">
                          <span className="step-number">1</span>
                          <span>申請書を選択してください</span>
                        </div>
                        <div className="step">
                          <span className="step-number">2</span>
                          <span>データ種別を選択してください</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Save Section */}
          <div className="save-section">
            <button
              className={`save-button ${!canSave() ? "disabled" : ""}`}
              onClick={handleSave}
              disabled={!canSave()}
            >
              {loading ? "保存中..." : "マッピング設定を保存"}
            </button>
            <p className="save-help-text">
              設定内容を確認して保存してください。
            </p>
          </div>
        </div>

        {/* Regenerate Webhook Token Confirmation Modal */}
        <ConfirmationModal
          isOpen={showRegenerateConfirmationModal}
          title="再生成の確認"
          message={`Webhook URLを再生成すると、以前のURLは無効になります。\n この操作は元に戻せません。`}
          confirmText="再生成"
          cancelText="キャンセル"
          confirmButtonColor="danger"
          onConfirm={confirmRegenerateWebhookToken}
          onCancel={() => setShowRegenerateConfirmationModal(false)}
        />

        {/* Invalid Settings Warning Modal (for 401/500 authentication errors) */}
        <ConfirmationModal
          isOpen={showInvalidSettingsWarning}
          title="設定が必要です"
          message="設定されたコラボフローの認証情報が正しくないか、無効になっている可能性があります。管理者設定でご確認ください。"
          confirmText="管理者設定へ"
          cancelText="キャンセル"
          confirmButtonColor="primary"
          onConfirm={handleInvalidSettingsConfirm}
          onCancel={handleInvalidSettingsCancel}
        />
      </div>
    );
  } catch (error) {
    console.error("Error in render:", error);
    return (
      <div className="mapping-form-container">
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h2>レンダリングエラーが発生しました</h2>
          <p>{error instanceof Error ? error.message : "Unknown error"}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 16px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            ページを再読み込み
          </button>
        </div>
      </div>
    );
  }
};

export default MappingRegistrationForm;
